// src/pages/account/AccountProfile.js - Platform account profile management
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  max-width: 500px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
  }
`;

const Button = styled.button`
  padding: 12px 20px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: #0056b3;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const AccountProfile = ({ user, userProfile }) => {
  const [anonymousUsername, setAnonymousUsername] = useState('');
  const [bio, setBio] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (userProfile) {
      setAnonymousUsername(userProfile.anonymous_username || '');
      setBio(userProfile.bio || '');
    }
  }, [userProfile]);

  const updateProfile = async () => {
    try {
      setSuccessMessage('');
      setErrorMessage('');
      
      const token = await user.getIdToken();
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/update-username`,
        { newUsername: anonymousUsername },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccessMessage('User info updated successfully!');
    } catch (error) {
      console.error('Error updating account profile:', error);
      setErrorMessage('Failed to update user info: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <ProfileContainer>
      <h1>User Info</h1>
      <p>Manage your platform account information and display preferences.</p>
      
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      
      <Section>
        <SectionTitle>👤 User Information</SectionTitle>
        
        <FormGroup>
          <Label>Display Username</Label>
          <Input
            type="text"
            value={anonymousUsername}
            onChange={(e) => setAnonymousUsername(e.target.value)}
            placeholder="Enter your display name"
          />
          <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
            This is the name shown to other users in the gallery and public areas
          </p>
        </FormGroup>
        
        <FormGroup>
          <Label>Bio</Label>
          <TextArea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell others about yourself"
            rows="4"
          />
          <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
            Visible on your public profile and in the gallery
          </p>
        </FormGroup>
        
        <FormGroup>
          <Label>Email Address</Label>
          <Input
            type="email"
            value={user?.email || ''}
            disabled
            style={{ background: '#f8f9fa', color: '#666' }}
          />
          <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
            Your login email cannot be changed here. Contact support if needed.
          </p>
        </FormGroup>
        
        <Button onClick={updateProfile}>
          💾 Save User Info
        </Button>
      </Section>
    </ProfileContainer>
  );
};

export default AccountProfile;